/**
 * Two Thousand — lightweight SPA navigation (pjax).
 * Intercepts same-origin link clicks, fetches the target page, and swaps
 * <main> without a full reload. Header, cart drawer, gradient canvas and
 * grain overlay live outside <main>, so menu state, animations and Alpine
 * stores persist across navigations. Uses View Transitions API when
 * available for a native crossfade.
 */
(function () {
  var MAIN_SEL = 'main#MainContent';

  function shouldIntercept(a) {
    if (!a || a.tagName !== 'A') return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    if (a.dataset.noPjax === 'true') return false;
    var href = a.getAttribute('href');
    if (!href) return false;
    if (href[0] === '#') return false;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return false;
    var url;
    try { url = new URL(a.href, location.origin); } catch (e) { return false; }
    if (url.origin !== location.origin) return false;
    // Skip Shopify routes that require real HTTP (cart mutations, checkout, files)
    if (/^\/cart(\/|$)/.test(url.pathname)) return false;
    if (/^\/checkout/.test(url.pathname)) return false;
    if (/^\/tools\//.test(url.pathname)) return false;
    if (/^\/apps\//.test(url.pathname)) return false;
    return true;
  }

  function reexecScripts(container) {
    var scripts = container.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var s = document.createElement('script');
      for (var j = 0; j < old.attributes.length; j++) {
        s.setAttribute(old.attributes[j].name, old.attributes[j].value);
      }
      if (old.textContent) s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    }
  }

  function swap(newDoc) {
    var newMain = newDoc.querySelector(MAIN_SEL);
    var oldMain = document.querySelector(MAIN_SEL);
    if (!newMain || !oldMain) return false;

    oldMain.replaceWith(newMain);

    var newTitle = newDoc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    var newDesc = newDoc.querySelector('meta[name="description"]');
    var curDesc = document.querySelector('meta[name="description"]');
    if (newDesc) {
      if (curDesc) curDesc.setAttribute('content', newDesc.getAttribute('content') || '');
      else document.head.appendChild(newDesc.cloneNode(true));
    }

    if (newDoc.body && newDoc.body.className) {
      document.body.className = newDoc.body.className;
    }

    // Reset gradient a los defaults del theme — SOLO si la nueva página no
    // es un PDP. En PDP la extracción de color se dispara desde el init y
    // sobreescribe. En cualquier otra página (contacto, políticas, colecciones)
    // volvemos al azul cielo default en lugar de arrastrar el color del
    // último producto visto.
    var newIsPdp = !!newMain.querySelector('.pdp');
    if (!newIsPdp && window.__twoThousandGradient && window.__twoThousandGradient.resetColor) {
      window.__twoThousandGradient.resetColor();
    }

    // IMPORTANT: re-exec scripts BEFORE initTree — otherwise inline scripts that
    // register Alpine components (e.g. Alpine.data('pdp', ...)) haven't run yet,
    // and Alpine will silently skip x-data="pdp(...)" nodes, leaving the PDP
    // stuck in its unblurred/default CSS state until a manual refresh.
    reexecScripts(newMain);
    if (window.Alpine && typeof Alpine.initTree === 'function') {
      Alpine.initTree(newMain);
    }

    window.scrollTo(0, 0);
    // PDP mobile has body:position:fixed with scroll on main#MainContent —
    // window.scrollTo doesn't reset it, so previous nav's scroll position
    // persists (the info card appears already scrolled up on load).
    if (newMain && typeof newMain.scrollTop === 'number') newMain.scrollTop = 0;
    return true;
  }

  /* Prefetch cache: URL → Promise<htmlText>. Hover starts the fetch; click
     reuses it instantly. TTL evicta entradas viejas (5min) para no acumular
     HTML stale si el user pasa el mouse por muchos productos sin clickear. */
  var prefetchCache = new Map();
  var PREFETCH_TTL_MS = 5 * 60 * 1000;

  function getPrefetched(url) {
    var entry = prefetchCache.get(url);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > PREFETCH_TTL_MS) {
      prefetchCache.delete(url);
      return null;
    }
    return entry.promise;
  }

  function prefetch(url) {
    if (prefetchCache.has(url)) return prefetchCache.get(url).promise;
    var promise = fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch', 'X-Pjax-Prefetch': '1' },
    })
      .then(function (res) { return res.ok ? res.text() : null; })
      .catch(function () { return null; });
    prefetchCache.set(url, { promise: promise, timestamp: Date.now() });
    return promise;
  }

  var navigating = false;
  function navigate(url, options) {
    options = options || {};
    if (navigating) return;
    navigating = true;

    document.documentElement.setAttribute('data-pjax-navigating', 'true');

    // Reuse prefetched HTML si existe (0 latencia de red — click instantáneo)
    var cached = getPrefetched(url);
    var fetchPromise = cached || fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch' },
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    });

    Promise.resolve(fetchPromise)
      .then(function (html) {
        if (!html) throw new Error('empty response');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var runSwap = function () {
          if (!swap(doc)) throw new Error('swap failed');
        };
        if (document.startViewTransition && !options.noTransition) {
          document.startViewTransition(runSwap);
        } else {
          runSwap();
        }
        if (options.push !== false) {
          history.pushState({ pjax: true, url: url }, '', url);
        }
      })
      .catch(function () {
        window.location.href = url; // hard fallback
      })
      .then(function () {
        navigating = false;
        document.documentElement.removeAttribute('data-pjax-navigating');
      });
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    var a = e.target.closest && e.target.closest('a');
    if (!shouldIntercept(a)) return;
    e.preventDefault();
    navigate(a.href);
  });

  /* Hover-preload: comienza a fetch el PDP cuando el mouse entra sobre un
     link. Delay de 65ms filtra hovers accidentales (mover el cursor sobre
     el producto rápido no dispara). Guardado en cache — click reutiliza. */
  var hoverTimer;
  document.addEventListener('mouseover', function (e) {
    if (!e.target || !e.target.closest) return;
    var a = e.target.closest('a');
    if (!shouldIntercept(a)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(a.href); }, 65);
  });
  document.addEventListener('mouseout', function () {
    clearTimeout(hoverTimer);
  });

  /* mousedown / touchstart: dispara prefetch INMEDIATO cuando el usuario
     empieza a clickear/tocar (antes del click event ~50-150ms). Máximo
     head-start posible sin trigger de hover (ej: mobile). */
  document.addEventListener('mousedown', function (e) {
    if (e.button !== 0) return;
    if (!e.target || !e.target.closest) return;
    var a = e.target.closest('a');
    if (!shouldIntercept(a)) return;
    prefetch(a.href);
  }, { passive: true });
  document.addEventListener('touchstart', function (e) {
    if (!e.target || !e.target.closest) return;
    var a = e.target.closest('a');
    if (!shouldIntercept(a)) return;
    prefetch(a.href);
  }, { passive: true });

  window.addEventListener('popstate', function () {
    navigate(location.href, { push: false });
  });

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  history.replaceState({ pjax: true, url: location.href }, '', location.href);

  /* Expose navigate + prefetch para que otros módulos (ej: mobile touch
     handler del grid) puedan disparar navegación pjax sin usar
     window.location.href (que fuerza reload completo). */
  window.__pjaxNavigate = navigate;
  window.__pjaxPrefetch = prefetch;
})();
